import { instantOf } from './freshness.js'
import { deriveYourProgress, episodeCode, hueForTitle, joinableId, subscriptionsOf } from './watch.js'
import { titlesOf } from './watchArchive.js'
import { reviewDisclaimer } from './reviewDisclaimer.js'
import { TARGET_STOPPED, TARGET_WATCHED, TARGET_WATCHING } from './watchRequests.js'

// P6-303b: `utils/watch_manager.py::COMPLETED_VIA_MOVIE_NIGHT`'s own wire
// value. No site-side constants module owns this slice's addition
// (`watchRequests.js` owns `TARGET_*`, a member's watch STATUS — a
// different vocabulary from a completion's PROVENANCE). Fix round
// (manager review): this used to be a second, independently-checked copy
// in `WatchTitle.jsx` too (gotcha 21's *copies* axis) — exported from here
// instead, the one file both `memberLogEntries` (below) and
// `WatchTitle.jsx::ArchivedMembersSection` import it and its own check
// from. Each call site keeps its OWN status condition (`entry.status ===
// TARGET_WATCHED`, `member.status === TARGET_WATCHED`) — only "is this
// completion's provenance a movie night" is shared, not "is this row a
// finish at all."
export const COMPLETED_VIA_MOVIE_NIGHT = 'movie_night'

/** Whether one watcher/log entry's own completion was made at movie
 * night — the ONE place this check is written, reused by both surfaces
 * that render the "at movie night" phrase. Pass any object carrying a
 * `completed_via` field (a raw watcher entry, an archive member, a log
 * entry) — this reads nothing else off it. */
export function completedAtMovieNight(entry) {
  return entry?.completed_via === COMPLETED_VIA_MOVIE_NIGHT
}

// The member profile's own data — everything `WatchMemberProfile.jsx` needs
// that is not already exported by `watch.js` or `watchArchive.js`. V2-2c,
// `docs/design-briefs/watch/v2/HANDOFF.md` screen 5 ("member," the show-off
// page).
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 THE ARCHIVE IS THE ONE COMPLETE SOURCE — `/api/watch` ENRICHES, NEVER
//    REPLACES — the exact shape `watchArchive.js::yourStatusLine` already
//    established for the archive page, one axis over
// ═══════════════════════════════════════════════════════════════════════════
//
// A profile is built for ONE MEMBER across EVERY TITLE they have ever
// touched — favourites, finished dates and reviews can all live on a title
// off the watchlist (V2-2a's own archive carries `favorite_rank`/
// `watched_when`/`review` per member on EVERY title, listed or not), so the
// archive (`watchArchive.js::titlesOf`) is this file's one source for "does
// this member have a fact on this title at all." `/api/watch`'s own summary
// is read ONLY to borrow a listed title's per-member `progress` — the one
// fact the archive deliberately excludes (`utils/watch_projection.py::
// _archive_title_view`'s own docstring) — for a currently-watching or
// stopped entry's optional position code. Never the other way around: this
// file never treats `/api/watch` as the list of titles to iterate, only as
// an optional enrichment keyed by id.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 A ROUTED MEMBER ID IS JUST A STRING TO MATCH FACTS AGAINST — NO
//    SEPARATE "IS THIS A REAL MEMBER" CHECK
// ═══════════════════════════════════════════════════════════════════════════
//
// There is no per-member endpoint (`site/functions/**` is outside this
// slice's ownership, and none exists) and no "member exists" signal beyond
// the directory, which already treats an unresolved id the same way
// everywhere else in this codebase: a neutral placeholder, never an error
// (`directory.js::UNKNOWN_NAME`). A `memberId` route param that matches
// nothing here (a typo, an id belonging to nobody, or simply a member with
// no watch activity at all) produces an honestly EMPTY profile — zero
// finished, no favourites, an empty log, no reviews — rather than a second,
// bespoke "not found" state. Every function below is a plain scan-and-match
// over already-fetched data; none of them can throw on a bad id.

function memberEntry(title, memberId) {
  const members = Array.isArray(title?.members) ? title.members : []
  return members.find((member) => joinableId(member?.id) === memberId) ?? null
}

/** How many titles this member has finished, across the whole archive —
 * the hero's own "N finished all time." */
export function memberFinishedCount(archiveShared, memberId) {
  let count = 0
  for (const title of titlesOf(archiveShared)) {
    if (memberEntry(title, memberId)?.status === TARGET_WATCHED) count += 1
  }
  return count
}

// ═══════════════════════════════════════════════════════════════════════════
// `watched_when` — {precision: day|month|year|none, value} -- never an ISO
// instant (`utils/watch_manager.py`'s own `PRECISION_*` constants). This is
// a bare CALENDAR value with no timezone attached, so it is parsed and
// formatted entirely in UTC -- never through `watch.js`'s Eastern
// formatters, which exist for real INSTANTS this store publishes elsewhere
// (`next_up.airs_at`, `news[].at`). Reading a calendar-only value as if it
// carried a timezone would shift the displayed day for no reason.
// ═══════════════════════════════════════════════════════════════════════════

const MONTH_ABBR = Object.freeze([
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
])

/** A sortable UTC instant for a `watched_when` object, or `null` when there
 * is nothing to sort by (no value at all, or precision `"none"` — a real,
 * deliberate value meaning "watched, but nobody recorded when," which is
 * exactly as unsortable as never having set one). */
export function watchedWhenSortKey(watchedWhen) {
  if (!watchedWhen || typeof watchedWhen !== 'object') return null
  const { precision, value } = watchedWhen
  if (typeof value !== 'string' || value === '') return null
  if (precision === 'day') {
    const [y, m, d] = value.split('-').map(Number)
    return [y, m, d].every(Number.isInteger) ? Date.UTC(y, m - 1, d) : null
  }
  if (precision === 'month') {
    const [y, m] = value.split('-').map(Number)
    return [y, m].every(Number.isInteger) ? Date.UTC(y, m - 1, 1) : null
  }
  if (precision === 'year') {
    const y = Number(value)
    return Number.isInteger(y) ? Date.UTC(y, 0, 1) : null
  }
  return null
}

/** `5 Mar 2024` / `Mar 2024` / `2024` / `undated`, from the SAME precision
 * `watchedWhenSortKey` just parsed — never guessed to a finer grain than
 * the member actually gave. */
export function watchedWhenLabel(watchedWhen) {
  const key = watchedWhenSortKey(watchedWhen)
  if (key === null) return 'undated'
  const date = new Date(key)
  const year = date.getUTCFullYear()
  if (watchedWhen.precision === 'year') return String(year)
  if (watchedWhen.precision === 'month') return `${MONTH_ABBR[date.getUTCMonth()]} ${year}`
  return `${date.getUTCDate()} ${MONTH_ABBR[date.getUTCMonth()]} ${year}`
}

/**
 * "Member since," or `null` when nothing honest exists — the brief's own
 * instruction: "decide what date the store honestly knows (e.g. the
 * member's earliest watch record) and state it. Never invent one."
 *
 * The store has no "joined watch-together" field of any kind (the design's
 * own prototype invents one, a hardcoded `JOINED` mock with no store
 * counterpart — see the report). The earliest DATED completion this member
 * has on record is the one honest proxy available: it is real, it is
 * theirs, and a member with zero finished titles, or whose every
 * completion is precision `"none"`, gets no line at all rather than a
 * fabricated one.
 */
export function memberSince(archiveShared, memberId) {
  let earliest = null
  for (const title of titlesOf(archiveShared)) {
    const entry = memberEntry(title, memberId)
    if (entry?.status !== TARGET_WATCHED) continue
    const key = watchedWhenSortKey(entry.watched_when)
    if (key !== null && (earliest === null || key < earliest)) earliest = key
  }
  if (earliest === null) return null
  const date = new Date(earliest)
  return `${MONTH_ABBR[date.getUTCMonth()]} ${date.getUTCFullYear()}`
}

// ═══════════════════════════════════════════════════════════════════════════
// FAVOURITES — ranked across the WHOLE archive (a favourite can be a title
// off the watchlist), never just the listed subscriptions
// ═══════════════════════════════════════════════════════════════════════════

/** Every title this member has ranked, ascending by rank — `{title, rank}`
 * pairs, `title` the archive's own row (`{id, kind, title, poster_url,
 * year, listed, members}`). Ranks are dense by construction
 * (`WatchManager.set_favorite_rank`'s own docstring), but this reads each
 * title's OWN rank rather than assuming `index + 1`, so a data anomaly
 * reads honestly instead of silently by position. */
export function memberFavorites(archiveShared, memberId) {
  const favorites = []
  for (const title of titlesOf(archiveShared)) {
    const rank = memberEntry(title, memberId)?.favorite_rank
    if (typeof rank === 'number' && Number.isFinite(rank)) favorites.push({ title, rank })
  }
  favorites.sort((a, b) => a.rank - b.rank)
  return favorites
}

/**
 * The rank a favourite move should ASK FOR — an adjacent swap, per
 * `WatchManager.set_favorite_rank`'s own reorder rule: setting rank N pulls
 * the title out of wherever it sits and reinserts it at N, shifting
 * everything from N onward down by one. For an ADJACENT move that is
 * exactly a swap, so the target rank is simply the NEIGHBOUR's own current
 * rank (read from the list, never assumed to equal `index ± 1`, in case a
 * future data anomaly ever makes the ranks non-dense).
 *
 * `direction` is `-1` (up) or `1` (down). `null` when there is no such
 * neighbour (already at the top, or the bottom, of the RENDERED list —
 * `favorites` is the caller's own, possibly-truncated top-10 slice, so
 * "no neighbour" here means "no visible button," matching the design's own
 * `canUp`/`canDown`).
 */
export function favoriteMoveTarget(favorites, index, direction) {
  const neighbourIndex = index + direction
  if (neighbourIndex < 0 || neighbourIndex >= favorites.length) return null
  return favorites[neighbourIndex].rank
}

/**
 * V2-4c: the heart's own "turn it on" rank — brief §2, "adds a favourite
 * at the NEXT FREE RANK." Reuses `memberFavorites` rather than re-deriving
 * it (CLAUDE.md gotcha 1's *copies* axis) so this reads the identical
 * whole-archive set the reorder buttons already trust.
 *
 * Deliberately `Math.max(...ranks) + 1`, never `favorites.length + 1` —
 * ranks are "dense by construction" per `memberFavorites`'s own comment,
 * but that comment also says to read each title's OWN rank rather than
 * assume one by position "in case a future data anomaly ever makes the
 * ranks non-dense." A length-based answer would silently reuse an
 * existing rank if that anomaly ever happened; a max-based one cannot.
 *
 * `1` when the member has no favourites yet at all (`Math.max()` of an
 * empty list is `-Infinity`, never a real rank to return).
 */
export function nextFavoriteRank(archiveShared, memberId) {
  const favorites = memberFavorites(archiveShared, memberId)
  if (favorites.length === 0) return 1
  return Math.max(...favorites.map((f) => f.rank)) + 1
}

// ═══════════════════════════════════════════════════════════════════════════
// THE LOG — every title with a status, newest first
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Decision, stated per the brief's own instruction: CURRENTLY-watching or
 * stopped titles sort first (there is no honest "when did this start"
 * date for either — `deriveYourProgress`'s own position is the closest
 * fact and it is not a date), then finished titles newest-completed-first
 * by `watched_when`, with an undated completion last. Ties within the
 * "current" group, and among undated finishes, break alphabetically by
 * title — a deterministic order needing no further store data, mirroring
 * the archive's own alphabetical fallback (`watchArchive.js`'s report,
 * "the one order needing no derived state at all").
 *
 * A listed title's currently-watching/stopped entry is enriched with the
 * member's own position (`deriveYourProgress`, reused verbatim — never a
 * second position derivation) when `watchShared` has a matching
 * subscription; an unlisted one reads the bare word, exactly like
 * `watchArchive.js::yourStatusLine`'s own rule for the identical shape.
 */
export function memberLogEntries(archiveShared, watchShared, memberId) {
  const listedById = new Map()
  for (const subscription of subscriptionsOf(watchShared)) listedById.set(subscription.id, subscription)

  const entries = []
  for (const title of titlesOf(archiveShared)) {
    const entry = memberEntry(title, memberId)
    if (!entry || typeof entry.status !== 'string') continue

    const base = {
      id: title.id,
      title: title.title,
      kind: title.kind,
      posterUrl: title.poster_url,
      hue: hueForTitle(title),
    }

    if (entry.status === TARGET_WATCHED) {
      const hasRating = typeof entry.rating === 'number' && Number.isFinite(entry.rating)
      // P6-303b: a completion made AT movie night reads as such, in the
      // SAME "· clause" style the rating suffix already uses — appended
      // last, so "Finished · 9/10 · at movie night" reads as one sentence
      // rather than two facts mashed together.
      const atMovieNight = completedAtMovieNight(entry)
      entries.push({
        ...base,
        current: false,
        dateKey: watchedWhenSortKey(entry.watched_when),
        when: watchedWhenLabel(entry.watched_when),
        detail: (hasRating ? `Finished · ${entry.rating}/10` : 'Finished') + (atMovieNight ? ' · at movie night' : ''),
      })
      continue
    }

    if (entry.status === TARGET_WATCHING || entry.status === TARGET_STOPPED) {
      const listed = listedById.get(title.id) ?? null
      const progress = listed ? deriveYourProgress(listed, memberId) : null
      const code = progress ? episodeCode(progress.season, progress.episode) : null
      const label = entry.status === TARGET_WATCHING ? 'Watching' : 'Stopped'
      entries.push({
        ...base,
        current: true,
        dateKey: null,
        when: entry.status === TARGET_WATCHING ? '● now' : 'stopped',
        detail: code ? `${label} · ${code}` : label,
      })
    }
  }

  entries.sort((a, b) => {
    if (a.current !== b.current) return a.current ? -1 : 1
    if (a.current) return a.title.localeCompare(b.title)
    if (a.dateKey === null && b.dateKey === null) return a.title.localeCompare(b.title)
    if (a.dateKey === null) return 1
    if (b.dateKey === null) return -1
    return b.dateKey - a.dateKey
  })

  return entries
}

// ═══════════════════════════════════════════════════════════════════════════
// REVIEWS — every review this member has written, across the whole archive
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Ordering, stated per the brief's own instruction: newest-updated first,
 * an undated review last, ties broken alphabetically by title — the same
 * "always a deterministic order, never a store re-read" shape the log
 * above uses, applied to `review.updated_at` (a real ISO instant, unlike
 * `watched_when`) instead of a calendar value.
 *
 * `episode_count` for `reviewDisclaimer`'s own "of N" clause is borrowed
 * from a matching LISTED subscription when one exists (the archive
 * deliberately excludes it, same reasoning as the log's position
 * enrichment above) — never guessed for an unlisted title.
 *
 * Returns the archive's own `title` row alongside the derived fields
 * (`archiveTitle`) so the caller can compute the VIEWER's own
 * finished-status per row via `watchArchive.js::yourArchiveEntry` without
 * this file re-deriving that question a second way.
 */
export function memberReviews(archiveShared, watchShared, memberId) {
  const listedById = new Map()
  for (const subscription of subscriptionsOf(watchShared)) listedById.set(subscription.id, subscription)

  const reviews = []
  for (const title of titlesOf(archiveShared)) {
    const entry = memberEntry(title, memberId)
    const text = entry?.review?.text
    if (typeof text !== 'string' || text.trim() === '') continue

    const listed = listedById.get(title.id) ?? null
    const episodeCount = typeof listed?.episode_count === 'number' ? listed.episode_count : null

    reviews.push({
      archiveTitle: title,
      id: title.id,
      title: title.title,
      posterUrl: title.poster_url,
      hue: hueForTitle(title),
      whenMs: instantOf(entry.review.updated_at),
      rating: typeof entry.rating === 'number' && Number.isFinite(entry.rating) ? entry.rating : null,
      text,
      disclaimer: reviewDisclaimer(entry.review.context ?? null, episodeCount),
    })
  }

  reviews.sort((a, b) => {
    if (a.whenMs === null && b.whenMs === null) return a.title.localeCompare(b.title)
    if (a.whenMs === null) return 1
    if (b.whenMs === null) return -1
    return b.whenMs - a.whenMs
  })

  return reviews
}
